// Bookmark list rendering and filtering, shared by the web UI and the
// extension sidebar. These two screens were ~85% the same code before this
// module existed; the differences that remain are passed in as options.
import { el, esc, hostname, formatDate } from './dom.ts';
import { isUploadUrl } from './upload.ts';
import { youtubeVideoId } from './youtube.ts';
import type { Bookmark, ContentType, ReadingStatus, Tag } from './types.ts';

/** What a bookmark is, coarser than ContentType: every kind of audio is one. */
export type DocType = 'html' | 'pdf' | 'epub' | 'youtube' | 'audio';

export const DOC_TYPES: DocType[] = ['html', 'pdf', 'epub', 'youtube', 'audio'];

const DOC_TYPE_OF: Record<ContentType, DocType> = {
  html: 'html', pdf: 'pdf', epub: 'epub', youtube: 'youtube',
  podcast: 'audio', podcast_feed: 'audio', audiobook: 'audio',
};

/**
 * The archived article's type when there is one; otherwise a guess from the
 * URL, so a bookmark that was never archived still lands under a filter.
 */
export function docType(bookmark: Bookmark): DocType {
  if (bookmark.content_type) return DOC_TYPE_OF[bookmark.content_type] ?? 'html';
  if (youtubeVideoId(bookmark.url)) return 'youtube';
  const path = (bookmark.url.split(/[?#]/)[0] ?? '').toLowerCase();
  if (path.endsWith('.pdf')) return 'pdf';
  if (path.endsWith('.epub')) return 'epub';
  if (/\.(mp3|m4a|m4b|ogg|opus|wav)$/.test(path)) return 'audio';
  return 'html';
}

export interface FilterOptions {
  query?: string;
  tag?: string | null;
  status?: ReadingStatus | null;
  type?: DocType | null;
}

/**
 * What a consumer supplies to render rows. Handlers are optional because the
 * two consumers differ: the extension sidebar has no reader of its own and no
 * bulk selection, so it passes neither.
 */
export interface ListHandlers {
  onOpenReader?: (bookmark: Bookmark) => void;
  onSelectTag?: (tag: string) => void;
  onDelete?: (bookmark: Bookmark) => void;
  onToggleSelect?: (bookmark: Bookmark, selected: boolean) => void;
  selected?: boolean;
  selectedIds?: Set<number> | number[];
  compact?: boolean;
  emptyMessage?: string;
}

export interface TagChipOptions {
  activeTag?: string | null;
  onSelectTag?: (tag: string) => void;
}

/**
 * Client-side filtering. The API can filter by tag and status too, but the
 * list is already in memory in both consumers, so filtering locally keeps
 * typing responsive without a request per keystroke.
 */
export function filterBookmarks(
  bookmarks: Bookmark[],
  { query = '', tag = null, status = null, type = null }: FilterOptions = {},
): Bookmark[] {
  let items = bookmarks;

  if (tag) items = items.filter((b) => (b.tags || []).includes(tag));

  if (type) items = items.filter((b) => docType(b) === type);

  if (status) {
    items = items.filter((b) => (b.reading_status || 'unread') === status);
  }

  const q = query.trim().toLowerCase();
  if (q) {
    items = items.filter((b) =>
      (b.title || '').toLowerCase().includes(q) ||
      (b.url || '').toLowerCase().includes(q) ||
      (b.note || '').toLowerCase().includes(q) ||
      (b.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  }

  return items;
}

const STATUS_LABEL: Record<ReadingStatus, string> = { unread: '', reading: 'reading', done: 'read' };

/**
 * One bookmark row.
 *
 * @param {object} bookmark
 * @param {object} handlers
 * @param {(b) => void} [handlers.onOpenReader] omitted where there's no reader
 *        to open (the extension sidebar links out to the web UI instead).
 * @param {(tag) => void} [handlers.onSelectTag]
 * @param {(b) => void} [handlers.onDelete]
 * @param {(b, selected) => void} [handlers.onToggleSelect] enables the
 *        checkbox; omitted where bulk actions don't apply (the sidebar).
 * @param {boolean} [handlers.compact]
 */
export function renderBookmarkItem(bookmark: Bookmark, handlers: ListHandlers = {}): HTMLElement {
  const {
    onOpenReader, onSelectTag, onDelete, onToggleSelect,
    selected = false, compact = false,
  } = handlers;
  const status = bookmark.reading_status || 'unread';
  const percent = Math.round((bookmark.reading_percent || 0) * 100);

  // An uploaded file has no page to link to; its title opens the reader.
  const isUpload = isUploadUrl(bookmark.url);
  const title = el('a', isUpload
    ? { class: 'bm-title', href: `#/read/${bookmark.id}` }
    : { class: 'bm-title', href: bookmark.url, target: '_blank', rel: 'noopener noreferrer', title: bookmark.url },
    bookmark.title || bookmark.url);

  const type = docType(bookmark);
  const meta = el('div', { class: 'bm-meta' }, [
    el('span', { class: `bm-type bm-type-${type}` }, type),
    el('span', { class: 'bm-host' }, hostname(bookmark.url)),
    bookmark.created_at && el('span', { class: 'bm-date' }, formatDate(bookmark.created_at)),
    status !== 'unread' && el('span', { class: `bm-status bm-status-${status}` },
      status === 'reading' && percent > 0 ? `${STATUS_LABEL[status]} ${percent}%` : STATUS_LABEL[status]),
  ]);

  const tags = (bookmark.tags || []).map((tag) =>
    el('button', {
      class: 'tag-chip',
      type: 'button',
      onclick: (e: Event) => {
        e.stopPropagation();
        onSelectTag?.(tag);
      },
    }, tag)
  );

  const actions = el('div', { class: 'bm-actions' }, [
    bookmark.has_article && onOpenReader && el('button', {
      class: 'bm-read',
      type: 'button',
      onclick: (e: Event) => {
        e.stopPropagation();
        onOpenReader(bookmark);
      },
    }, status === 'reading' ? 'continue' : 'read'),
    onDelete && el('button', {
      class: 'bm-delete',
      type: 'button',
      title: 'move to trash',
      onclick: (e: Event) => {
        e.stopPropagation();
        onDelete(bookmark);
      },
    }, '✕'),
  ]);

  const checkbox = onToggleSelect
    ? el('input', {
        type: 'checkbox',
        class: 'bm-select',
        checked: selected,
        'aria-label': `Select ${bookmark.title || bookmark.url}`,
        onchange: (e: Event) => onToggleSelect(bookmark, (e.target as HTMLInputElement).checked),
      })
    : null;

  return el('div', {
    class: `bm-item${compact ? ' compact' : ''}` +
      `${bookmark.has_article ? ' has-article' : ''}${selected ? ' selected' : ''}`,
    dataset: { id: String(bookmark.id) },
  }, [
    el('div', { class: 'bm-head' }, [checkbox, title, actions]),
    meta,
    tags.length ? el('div', { class: 'bm-tags' }, tags) : null,
  ]);
}

/**
 * Replace a container's contents with the rendered list.
 * `selectedIds` may be a Set or an array of ids to show as selected.
 */
export function renderBookmarkList(
  container: HTMLElement,
  bookmarks: Bookmark[],
  handlers: ListHandlers = {},
): void {
  container.replaceChildren();
  if (!bookmarks.length) {
    container.append(el('div', { class: 'msg' }, handlers.emptyMessage || 'nothing here.'));
    return;
  }
  const selected = handlers.selectedIds instanceof Set
    ? handlers.selectedIds
    : new Set(handlers.selectedIds || []);

  for (const bookmark of bookmarks) {
    container.append(renderBookmarkItem(bookmark, {
      ...handlers,
      selected: selected.has(bookmark.id),
    }));
  }
}

/** Tag filter chips with counts. `tags` is the /tags response. */
export function renderTagChips(
  container: HTMLElement,
  tags: Tag[],
  { activeTag, onSelectTag }: TagChipOptions = {},
): void {
  container.replaceChildren();
  for (const tag of tags) {
    container.append(el('button', {
      class: `tag-chip${activeTag === tag.name ? ' active' : ''}`,
      type: 'button',
      onclick: () => onSelectTag?.(tag.name),
    }, [tag.name, el('span', { class: 'tag-count' }, tag.count)]));
  }
}

export { esc };
